import { Component, ElementRef, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { RouteReplayDataService } from '../../services/route-replay-data.service';
import { RouteReplayMapControlService, VehiclePosition } from '../../services/route-replay-map-control.service';
import { Vehicle } from '../../models/route-replay-response.model';
import { Subscription } from 'rxjs';

export interface VehicleSpeedData {
  vehicleId: string;
  registration: string;
  speed: number;
  color: string;
}

@Component({
  selector: 'app-map-view',
  templateUrl: './map-view.component.html',
  styleUrl: './map-view.component.scss'
})
export class MapViewComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: false }) mapElement!: ElementRef;

  private map!: google.maps.Map;
  private vehiclePolylines = new Map<string, google.maps.Polyline>();
  private vehicleMarkers = new Map<string, google.maps.Marker>();
  private milestoneMarkers: google.maps.Marker[] = [];
  private tripFlagMarkers: google.maps.Marker[] = [];
  private subscriptions: Subscription[] = [];
  private selectedVehicles: Vehicle[] = [];

  vehicleSpeedData: VehicleSpeedData[] = [];

  constructor(
    private routeReplayDataService: RouteReplayDataService,
    private mapControlService: RouteReplayMapControlService
  ) {}

  ngOnInit(): void {
    // Subscribe to selected vehicles
    const vehiclesSub = this.routeReplayDataService.selectedVehicles$.subscribe(vehicles => {
      this.selectedVehicles = vehicles;
      
      if (this.map) {
        this.clearMap();
        
        if (vehicles.length > 0) {
          this.plotSelectedVehicles(vehicles);
          this.initializeVehicleSpeedData(vehicles);
        } else {
          // Clear speed data when no vehicles selected
          this.vehicleSpeedData = [];
        }
      }
    });
    this.subscriptions.push(vehiclesSub);

    // Subscribe to vehicle position updates
    const positionsSub = this.mapControlService.vehiclePositions$.subscribe(positions => {
      this.updateVehicleMarkers(positions);
      this.updateVehicleSpeedData(positions);
    });
    this.subscriptions.push(positionsSub);
  }

  initializeVehicleSpeedData(vehicles: Vehicle[]): void {
    this.vehicleSpeedData = vehicles.map(vehicle => ({
      vehicleId: vehicle.etsVehicle.guId,
      registration: vehicle.etsVehicle.registration,
      speed: 0,
      color: vehicle.color || '#638FE7'
    }));
  }

  updateVehicleSpeedData(positions: Map<string, VehiclePosition>): void {
    this.vehicleSpeedData = this.vehicleSpeedData.map(data => {
      const position = positions.get(data.vehicleId);
      return {
        ...data,
        speed: position ? Math.floor(position.speed) : data.speed
      };
    });
  }

  getSpeedBarColor(speed: number): string {
    if (speed >= 0 && speed <= 60) {
      return '#55BF3B'; // Green
    } else if (speed > 60 && speed <= 80) {
      return '#DDDF0D'; // Yellow
    } else {
      return '#DF5353'; // Red
    }
  }

  getSpeedBarWidth(speed: number): number {
    return Math.min((speed / 140) * 100, 100);
  }

  ngAfterViewInit(): void {
    this.initializeMap();
  }

  initializeMap(): void {
    const mapOptions: google.maps.MapOptions = {
      center: { lat: 12.9716, lng: 77.5946 }, // MG Road, Bangalore
      zoom: 12,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true
    };

    this.map = new google.maps.Map(this.mapElement.nativeElement, mapOptions);
  }

  plotSelectedVehicles(vehicles: Vehicle[]): void {
    vehicles.forEach((vehicle, index) => {
      if (!vehicle.milestonesData || !vehicle.milestonesData.locationList) return;

      // Create polyline path
      const path = vehicle.milestonesData.locationList.map((loc: any) =>
        new google.maps.LatLng(loc.lat, loc.lng)
      );

      // Create polyline
      const polyline = new google.maps.Polyline({
        path: path,
        strokeColor: vehicle.color || '#638FE7',
        strokeWeight: 4,
        strokeOpacity: 0.8,
        map: this.map
      });

      this.vehiclePolylines.set(vehicle.etsVehicle.guId, polyline);

      // Plot milestones
      this.plotVehicleMilestones(vehicle);

      // Plot trip start and end flags
      this.plotTripFlags(vehicle);

      // Create vehicle marker
      this.createVehicleMarker(vehicle);
    });

    // Fit bounds to show all routes
    this.fitMapBounds();
  }

  plotVehicleMilestones(vehicle: Vehicle): void {
    if (!vehicle.milestonesData || !vehicle.milestonesData.events) return;

    vehicle.milestonesData.events.forEach((event: any) => {
      const location = this.findLocationAtTime(
        vehicle.milestonesData.locationList,
        event.time
      );

      if (!location) return;

      const marker = new google.maps.Marker({
        position: new google.maps.LatLng(location.lat, location.lng),
        map: this.map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: vehicle.color || '#638FE7',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
          scale: 6
        },
        title: `${vehicle.etsVehicle.registration}: ${event.type}`,
        zIndex: 10
      });

      // Info window
      const infoContent = `
        <div style="padding: 8px;">
          <strong>${vehicle.etsVehicle.registration}</strong><br>
          <span style="color: #666;">${event.type}</span><br>
          <span style="font-size: 12px;">${event.location || ''}</span>
          ${event.count ? `<br><span style="font-size: 12px;">Count: ${event.count}</span>` : ''}
        </div>
      `;

      const infoWindow = new google.maps.InfoWindow({
        content: infoContent
      });

      marker.addListener('click', () => {
        infoWindow.open(this.map, marker);
      });

      this.milestoneMarkers.push(marker);
    });
  }

  createVehicleMarker(vehicle: Vehicle): void {
    if (!vehicle.milestonesData || !vehicle.milestonesData.locationList.length) return;

    const firstLocation = vehicle.milestonesData.locationList[0];

    // Use bus SVG icon with vehicle color
    const marker = new google.maps.Marker({
      position: new google.maps.LatLng(firstLocation.lat, firstLocation.lng),
      map: this.map,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(this.getBusSvg(vehicle.color || '#638FE7'))}`,
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 20)
      },
      title: vehicle.etsVehicle.registration,
      zIndex: 100
    });

    this.vehicleMarkers.set(vehicle.etsVehicle.guId, marker);
  }

  getBusSvg(color: string): string {
    // Use the actual bus SVG with dynamic color fill
    return `<svg width="25" height="55" viewBox="0 0 25 55" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19.9839 2.93036C18.5365 2.21592 5.38001 2.21592 3.93264 2.93036C2.48527 3.64479 0.928711 12.4554 0.928711 12.4554V54.5003H22.9878V12.4554C22.9878 12.4554 21.4318 3.64434 19.9839 2.93036Z" fill="black" fill-opacity="0.38"/><path d="M21.8613 1.49286C20.414 0.778422 7.25745 0.778422 5.81008 1.49286C4.36271 2.20729 2.80615 11.0179 2.80615 11.0179V53.0628H24.8653V11.0179C24.8653 11.0179 23.3092 2.20684 21.8613 1.49286Z" fill="${color}"/><path d="M20.825 6.22443C19.6856 5.57623 8.15471 5.57623 7.01528 6.22443C5.87586 6.87263 5.15283 14.902 5.15283 14.902V53.0637H22.5185V14.902C22.5185 14.902 21.9644 6.87263 20.825 6.22443Z" fill="#F8C52E"/><path d="M5.06236 5.3801C4.86331 5.63854 4.38729 7.22606 4.1178 8.56221C3.8483 9.89836 3.225 14.8434 3.47741 15.0231C3.72983 15.2028 4.42678 15.2848 4.71922 15.0231C5.01166 14.7614 5.231 11.3377 5.40337 10.1812C5.683 8.31789 6.18837 7.23366 5.96851 6.92365C5.74864 6.61364 5.36068 4.99136 5.06236 5.3801Z" fill="#010F55"/><path d="M4.72113 23.9204C4.72113 24.2597 4.41535 24.5355 4.03806 24.5355C3.66076 24.5355 3.35498 24.2597 3.35498 23.9204V16.3813C3.35498 16.0414 3.66076 15.7656 4.03806 15.7656C4.41535 15.7656 4.72113 16.0414 4.72113 16.3813V23.9204Z" fill="#010F55"/><path d="M4.72113 33.3105C4.72113 33.6504 4.41535 33.9256 4.03806 33.9256C3.66076 33.9256 3.35498 33.6504 3.35498 33.3105V25.7714C3.35498 25.4315 3.66076 25.1562 4.03806 25.1562C4.41535 25.1562 4.72113 25.4315 4.72113 25.7714V33.3105Z" fill="#010F55"/><path d="M4.72113 42.7045C4.72113 43.0444 4.41535 43.3196 4.03806 43.3196C3.66076 43.3196 3.35498 43.0444 3.35498 42.7045V35.1659C3.35498 34.826 3.66076 34.5508 4.03806 34.5508C4.41535 34.5508 4.72113 34.826 4.72113 35.1659V42.7045Z" fill="#010F55"/><path d="M4.72113 52.0985C4.72113 52.4378 4.41535 52.7136 4.03806 52.7136C3.66076 52.7136 3.35498 52.4378 3.35498 52.0985V44.5605C3.35498 44.2206 3.66076 43.9453 4.03806 43.9453C4.41535 43.9453 4.72113 44.2206 4.72113 44.5605V52.0985Z" fill="#010F55"/><path d="M22.6084 5.14968C22.8074 5.40812 23.2834 6.99564 23.5529 8.33233C23.8224 9.66902 24.4457 14.6129 24.1933 14.7932C23.9409 14.9734 23.244 15.0554 22.9515 14.7932C22.6591 14.5309 22.4424 11.1067 22.269 9.94917C21.9893 8.08584 21.484 7.00161 21.7038 6.6916C21.9237 6.38159 22.3079 4.76095 22.6084 5.14968Z" fill="#010F55"/><path d="M22.9482 23.6833C22.9482 24.0232 23.254 24.2985 23.6313 24.2985C24.0086 24.2985 24.3144 24.0232 24.3144 23.6833V16.1464C24.3144 15.8065 24.0086 15.5312 23.6313 15.5312C23.254 15.5312 22.9482 15.8065 22.9482 16.1464V23.6833Z" fill="#010F55"/><path d="M22.9482 33.0784C22.9482 33.4177 23.254 33.6935 23.6313 33.6935C24.0086 33.6935 24.3144 33.4177 24.3144 33.0784V25.5415C24.3144 25.2016 24.0086 24.9258 23.6313 24.9258C23.254 24.9258 22.9482 25.2016 22.9482 25.5415V33.0784Z" fill="#010F55"/><path d="M22.9482 42.4724C22.9482 42.8122 23.254 43.0875 23.6313 43.0875C24.0086 43.0875 24.3144 42.8122 24.3144 42.4724V34.9354C24.3144 34.5956 24.0086 34.3203 23.6313 34.3203C23.254 34.3203 22.9482 34.5956 22.9482 34.9354V42.4724Z" fill="#010F55"/><path d="M22.9482 51.863C22.9482 52.2029 23.254 52.4782 23.6313 52.4782C24.0086 52.4782 24.3144 52.2029 24.3144 51.863V44.3261C24.3144 43.9862 24.0086 43.7109 23.6313 43.7109C23.254 43.7109 22.9482 43.9862 22.9482 44.3261V51.863Z" fill="#010F55"/><path d="M21.0356 3.61278L22.4253 2.95312L24.8651 3.04705L24.8032 3.3929L22.5486 3.23545L21.3131 4.11554L21.0356 3.61278Z" fill="#40413C"/><path d="M6.63565 3.61278L5.24602 2.95312L2.80615 3.04705L2.86806 3.3929L5.12274 3.23545L6.35815 4.11554L6.63565 3.61278Z" fill="#40413C"/><path d="M6.04095 3.19391C5.58148 3.44638 6.6221 6.00629 6.6221 6.00629C6.6221 6.00629 11.1944 5.45955 13.2997 5.45955C19.251 5.45955 20.5574 6.26146 20.9208 6.00683C21.2842 5.75219 21.7907 3.31064 21.6103 3.07067C21.2111 2.53697 17.8849 2.18352 14.0666 2.17212C9.59776 2.158 6.83983 2.75577 6.04095 3.19391Z" fill="#010F55"/><path d="M14.0704 51.3684C14.0704 51.4381 14.0457 51.505 14.0018 51.5543C13.9578 51.6036 13.8982 51.6314 13.8359 51.6315C13.7737 51.6315 13.7139 51.6038 13.6699 51.5544C13.6258 51.5051 13.6011 51.4382 13.6011 51.3684V7.91495C13.6011 7.88043 13.6072 7.84624 13.619 7.81435C13.6308 7.78246 13.6481 7.75349 13.6699 7.7291C13.6917 7.70471 13.7176 7.68537 13.7461 7.6722C13.7746 7.65903 13.8051 7.65228 13.8359 7.65234C13.8981 7.65234 13.9578 7.68001 14.0017 7.72926C14.0457 7.77851 14.0704 7.8453 14.0704 7.91495V51.3684Z" fill="${color}"/><path d="M11.254 51.3684C11.254 51.4381 11.2293 51.505 11.1854 51.5543C11.1414 51.6036 11.0818 51.6314 11.0195 51.6315C10.9572 51.6315 10.8975 51.6038 10.8535 51.5544C10.8094 51.5051 10.7847 51.4382 10.7847 51.3684V7.91495C10.7847 7.88043 10.7907 7.84624 10.8026 7.81435C10.8144 7.78246 10.8317 7.75349 10.8535 7.7291C10.8753 7.70471 10.9012 7.68537 10.9297 7.6722C10.9582 7.65903 10.9887 7.65228 11.0195 7.65234C11.0817 7.65234 11.1414 7.68001 11.1853 7.72926C11.2293 7.77851 11.254 7.8453 11.254 7.91495V51.3684Z" fill="${color}"/><path d="M8.43809 51.3684C8.43809 51.4381 8.4134 51.505 8.36944 51.5543C8.32548 51.6036 8.26585 51.6314 8.20362 51.6315C8.14133 51.6315 8.08159 51.6038 8.03754 51.5544C7.9935 51.5051 7.96875 51.4382 7.96875 51.3684V7.91495C7.96875 7.88043 7.97483 7.84624 7.98664 7.81435C7.99845 7.78246 8.01575 7.75349 8.03757 7.7291C8.05938 7.70471 8.08528 7.68537 8.11377 7.6722C8.14226 7.65903 8.1728 7.65228 8.20362 7.65234C8.26581 7.65234 8.32545 7.68001 8.36942 7.72926C8.41339 7.77851 8.43809 7.8453 8.43809 7.91495V51.3684Z" fill="${color}"/><path d="M19.7023 51.3684C19.7023 51.4381 19.6776 51.505 19.6336 51.5543C19.5896 51.6036 19.53 51.6314 19.4678 51.6315C19.4055 51.6315 19.3457 51.6038 19.3017 51.5544C19.2577 51.5051 19.2329 51.4382 19.2329 51.3684V7.91495C19.2329 7.88043 19.239 7.84624 19.2508 7.81435C19.2626 7.78246 19.2799 7.75349 19.3017 7.7291C19.3235 7.70471 19.3494 7.68537 19.3779 7.6722C19.4064 7.65903 19.437 7.65228 19.4678 7.65234C19.53 7.65234 19.5896 7.68001 19.6336 7.72926C19.6775 7.77851 19.7023 7.8453 19.7023 7.91495V51.3684Z" fill="${color}"/><path d="M16.8863 51.3684C16.8862 51.4382 16.8615 51.505 16.8175 51.5543C16.7735 51.6036 16.7139 51.6314 16.6517 51.6315C16.5895 51.6314 16.5298 51.6036 16.4858 51.5543C16.4419 51.505 16.4171 51.4382 16.417 51.3684V7.91495C16.4171 7.84526 16.4419 7.77847 16.4859 7.72923C16.5299 7.68 16.5895 7.65234 16.6517 7.65234C16.7138 7.65234 16.7735 7.68 16.8175 7.72923C16.8615 7.77847 16.8862 7.84526 16.8863 7.91495V51.3684Z" fill="${color}"/><path d="M18.0876 14.7539H9.52135C9.10991 14.7539 8.77637 15.0932 8.77637 15.5118V22.609C8.77637 23.0276 9.10991 23.3669 9.52135 23.3669H18.0876C18.499 23.3669 18.8325 23.0276 18.8325 22.609V15.5118C18.8325 15.0932 18.499 14.7539 18.0876 14.7539Z" fill="#D4900C"/><path d="M18.0876 34.3203H9.52135C9.10991 34.3203 8.77637 34.6596 8.77637 35.0782V42.1754C8.77637 42.594 9.10991 42.9333 9.52135 42.9333H18.0876C18.499 42.9333 18.8325 42.594 18.8325 42.1754V35.0782C18.8325 34.6596 18.499 34.3203 18.0876 34.3203Z" fill="#D4900C"/><path d="M18.0695 14.375H9.50328C9.09184 14.375 8.7583 14.7143 8.7583 15.1329V22.2301C8.7583 22.6487 9.09184 22.988 9.50328 22.988H18.0695C18.4809 22.988 18.8145 22.6487 18.8145 22.2301V15.1329C18.8145 14.7143 18.4809 14.375 18.0695 14.375Z" fill="${color}"/><path d="M18.07 33.9414H9.50377C9.09233 33.9414 8.75879 34.2807 8.75879 34.6993V41.7965C8.75879 42.2151 9.09233 42.5544 9.50377 42.5544H18.07C18.4814 42.5544 18.815 42.2151 18.815 41.7965V34.6993C18.815 34.2807 18.4814 33.9414 18.07 33.9414Z" fill="${color}"/><path d="M9.16799 1.28193C9.16806 1.3076 9.16316 1.33303 9.15357 1.35677C9.14398 1.38051 9.12989 1.4021 9.1121 1.4203C9.09431 1.4385 9.07317 1.45296 9.04988 1.46285C9.0266 1.47273 9.00164 1.47786 8.97641 1.47793H6.3615C6.33627 1.47786 6.31131 1.47273 6.28802 1.46285C6.26474 1.45296 6.2436 1.4385 6.22581 1.4203C6.20802 1.4021 6.19393 1.38051 6.18434 1.35677C6.17475 1.33303 6.16985 1.3076 6.16992 1.28193C6.16985 1.25627 6.17475 1.23084 6.18434 1.2071C6.19393 1.18335 6.20802 1.16177 6.22581 1.14357C6.2436 1.12537 6.26474 1.11091 6.28802 1.10102C6.31131 1.09113 6.33627 1.08601 6.3615 1.08594H8.97641C9.00164 1.08601 9.0266 1.09113 9.04988 1.10102C9.07317 1.11091 9.09431 1.12537 9.1121 1.14357C9.12989 1.16177 9.14398 1.18335 9.15357 1.2071C9.16316 1.23084 9.16806 1.25627 9.16799 1.28193Z" fill="#010F55"/><path d="M9.06097 1.08638C8.89554 0.737276 8.33467 0.480469 7.66867 0.480469C7.00267 0.480469 6.44127 0.737276 6.27637 1.08638H9.06097Z" fill="#FAFAFA"/><path d="M21.5282 1.28193C21.5284 1.30758 21.5236 1.33299 21.514 1.35673C21.5045 1.38047 21.4905 1.40206 21.4727 1.42027C21.455 1.43847 21.4339 1.45294 21.4106 1.46284C21.3873 1.47273 21.3624 1.47786 21.3372 1.47793H18.7223C18.6971 1.47786 18.6722 1.47273 18.6489 1.46284C18.6256 1.45294 18.6045 1.43847 18.5868 1.42027C18.5691 1.40206 18.555 1.38047 18.5455 1.35673C18.5359 1.33299 18.5311 1.30758 18.5313 1.28193C18.5311 1.23019 18.5511 1.1805 18.587 1.14376C18.6228 1.10702 18.6714 1.08622 18.7223 1.08594H21.3372C21.3881 1.08622 21.4367 1.10702 21.4725 1.14376C21.5084 1.1805 21.5284 1.23019 21.5282 1.28193Z" fill="#010F55"/><path d="M21.4218 1.08638C21.2563 0.737276 20.696 0.480469 20.03 0.480469C19.364 0.480469 18.8026 0.737276 18.6377 1.08638H21.4218Z" fill="#FAFAFA"/></svg>`;
  }

  plotTripFlags(vehicle: Vehicle): void {
    if (!vehicle.milestonesData || !vehicle.milestonesData.locationList.length) return;

    const locationList = vehicle.milestonesData.locationList;
    const firstLocation = locationList[0];
    const lastLocation = locationList[locationList.length - 1];
    const vehicleColor = vehicle.color || '#638FE7';

    // Trip Start Flag - using trip-start-on-map.svg with dynamic color
    const startFlagSvg = `<svg width="88" height="75" viewBox="0 0 88 75" fill="none" xmlns="http://www.w3.org/2000/svg"><g filter="url(#filter0_dd_4518_2420)"><circle cx="44" cy="19" r="19" fill="white"/><circle cx="44" cy="19" r="19.5" stroke="#181F33"/></g><path d="M51.155 16.8053L55.0386 10.4468C55.312 9.99781 55.1696 9.41216 54.7205 9.13882C54.572 9.04839 54.4015 9.00039 54.2276 9H35.9519C35.4262 9 35 9.42616 35 9.95187V30.5121C35 31.0378 35.4262 31.464 35.9519 31.464C36.4776 31.464 36.9037 31.0378 36.9037 30.5121V24.6106H54.2276C54.7534 24.6095 55.1786 24.1824 55.1775 23.6567C55.1771 23.4827 55.1291 23.3123 55.0386 23.1638L51.155 16.8053Z" fill="${vehicleColor}"/><defs><filter id="filter0_dd_4518_2420" x="0" y="-13" width="88" height="88" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/><feOffset dy="4"/><feGaussianBlur stdDeviation="4"/><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/><feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_4518_2420"/><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/><feOffset dy="12"/><feGaussianBlur stdDeviation="12"/><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/><feBlend mode="normal" in2="effect1_dropShadow_4518_2420" result="effect2_dropShadow_4518_2420"/><feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_4518_2420" result="shape"/></filter></defs></svg>`;
    
    const startMarker = new google.maps.Marker({
      position: new google.maps.LatLng(firstLocation.lat, firstLocation.lng),
      map: this.map,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(startFlagSvg)}`,
        scaledSize: new google.maps.Size(44, 38),
        anchor: new google.maps.Point(22, 19)
      },
      title: `${vehicle.etsVehicle.registration} - Trip Start`,
      zIndex: 50
    });

    const startInfoWindow = new google.maps.InfoWindow({
      content: `
        <div style="padding: 8px;">
          <strong>${vehicle.etsVehicle.registration}</strong><br>
          <span style="color: #666;">Trip Start</span><br>
          <span style="font-size: 12px;">${vehicle.startTime}</span>
        </div>
      `
    });

    startMarker.addListener('click', () => {
      startInfoWindow.open(this.map, startMarker);
    });

    this.tripFlagMarkers.push(startMarker);

    // Trip End Flag - using trip-end-on-map.svg with dynamic color
    const endFlagSvg = `<svg width="88" height="88" viewBox="0 0 88 88" fill="none" xmlns="http://www.w3.org/2000/svg"><g filter="url(#filter0_dd_4518_2408)"><circle cx="44" cy="32" r="19" fill="white"/><circle cx="44" cy="32" r="19.5" stroke="#181F33"/></g><path d="M35.3179 19.4302C34.9152 19.4302 34.5889 19.7565 34.5889 20.1592V43.0012C34.5889 43.4038 34.9152 43.7302 35.3179 43.7302H36.5329C36.9355 43.7302 37.2619 43.4038 37.2619 43.0012V20.1592C37.2619 19.7565 36.9355 19.4302 36.5329 19.4302H35.3179Z" fill="${vehicleColor}"/><path d="M38.9629 19.4302C38.5603 19.4302 38.2339 19.7565 38.2339 20.1592V25.0192H44.7949V19.4302H38.9629Z" fill="${vehicleColor}"/><path d="M52.5708 25.0192V20.1592C52.5708 19.7565 52.2445 19.4302 51.8418 19.4302H45.7668V25.0192H52.5708Z" fill="${vehicleColor}"/><path d="M52.5708 25.991H45.7668V31.58H51.8418C52.2445 31.58 52.5708 31.2536 52.5708 30.851V25.991Z" fill="${vehicleColor}"/><path d="M44.7949 25.991H38.2339V30.851C38.2339 31.2536 38.5603 31.58 38.9629 31.58H44.7949V25.991Z" fill="${vehicleColor}"/><defs><filter id="filter0_dd_4518_2408" x="0" y="0" width="88" height="88" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/><feOffset dy="4"/><feGaussianBlur stdDeviation="4"/><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/><feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_4518_2408"/><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/><feOffset dy="12"/><feGaussianBlur stdDeviation="12"/><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/><feBlend mode="normal" in2="effect1_dropShadow_4518_2408" result="effect2_dropShadow_4518_2408"/><feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_4518_2408" result="shape"/></filter></defs></svg>`;
    
    const endMarker = new google.maps.Marker({
      position: new google.maps.LatLng(lastLocation.lat, lastLocation.lng),
      map: this.map,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(endFlagSvg)}`,
        scaledSize: new google.maps.Size(44, 44),
        anchor: new google.maps.Point(22, 32)
      },
      title: `${vehicle.etsVehicle.registration} - Trip End`,
      zIndex: 50
    });

    const endInfoWindow = new google.maps.InfoWindow({
      content: `
        <div style="padding: 8px;">
          <strong>${vehicle.etsVehicle.registration}</strong><br>
          <span style="color: #666;">Trip End</span><br>
          <span style="font-size: 12px;">${vehicle.endTime}</span>
        </div>
      `
    });

    endMarker.addListener('click', () => {
      endInfoWindow.open(this.map, endMarker);
    });

    this.tripFlagMarkers.push(endMarker);
  }

  updateVehicleMarkers(positions: Map<string, VehiclePosition>): void {
    positions.forEach((position, vehicleId) => {
      const marker = this.vehicleMarkers.get(vehicleId);
      if (marker) {
        marker.setPosition(new google.maps.LatLng(position.lat, position.lng));
        
        // Update icon with rotation if heading is available
        if (position.heading !== undefined) {
          const vehicle = this.selectedVehicles.find((v: Vehicle) => v.etsVehicle.guId === vehicleId);
          if (vehicle) {
            const icon = marker.getIcon() as google.maps.Icon;
            if (icon) {
              // Create rotated SVG
              const rotatedSvg = this.getBusSvgWithRotation(vehicle.color || '#638FE7', position.heading);
              marker.setIcon({
                ...icon,
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(rotatedSvg)}`
              });
            }
          }
        }
      }
    });
  }

  getBusSvgWithRotation(color: string, rotation: number): string {
    return `<svg width="25" height="55" viewBox="0 0 25 55" fill="none" xmlns="http://www.w3.org/2000/svg"><g transform="rotate(${rotation} 12.5 27.5)"><path d="M19.9839 2.93036C18.5365 2.21592 5.38001 2.21592 3.93264 2.93036C2.48527 3.64479 0.928711 12.4554 0.928711 12.4554V54.5003H22.9878V12.4554C22.9878 12.4554 21.4318 3.64434 19.9839 2.93036Z" fill="black" fill-opacity="0.38"/><path d="M21.8613 1.49286C20.414 0.778422 7.25745 0.778422 5.81008 1.49286C4.36271 2.20729 2.80615 11.0179 2.80615 11.0179V53.0628H24.8653V11.0179C24.8653 11.0179 23.3092 2.20684 21.8613 1.49286Z" fill="${color}"/><path d="M20.825 6.22443C19.6856 5.57623 8.15471 5.57623 7.01528 6.22443C5.87586 6.87263 5.15283 14.902 5.15283 14.902V53.0637H22.5185V14.902C22.5185 14.902 21.9644 6.87263 20.825 6.22443Z" fill="#F8C52E"/><path d="M5.06236 5.3801C4.86331 5.63854 4.38729 7.22606 4.1178 8.56221C3.8483 9.89836 3.225 14.8434 3.47741 15.0231C3.72983 15.2028 4.42678 15.2848 4.71922 15.0231C5.01166 14.7614 5.231 11.3377 5.40337 10.1812C5.683 8.31789 6.18837 7.23366 5.96851 6.92365C5.74864 6.61364 5.36068 4.99136 5.06236 5.3801Z" fill="#010F55"/><path d="M14.0704 51.3684C14.0704 51.4381 14.0457 51.505 14.0018 51.5543C13.9578 51.6036 13.8982 51.6314 13.8359 51.6315C13.7737 51.6315 13.7139 51.6038 13.6699 51.5544C13.6258 51.5051 13.6011 51.4382 13.6011 51.3684V7.91495C13.6011 7.88043 13.6072 7.84624 13.619 7.81435C13.6308 7.78246 13.6481 7.75349 13.6699 7.7291C13.6917 7.70471 13.7176 7.68537 13.7461 7.6722C13.7746 7.65903 13.8051 7.65228 13.8359 7.65234C13.8981 7.65234 13.9578 7.68001 14.0017 7.72926C14.0457 7.77851 14.0704 7.8453 14.0704 7.91495V51.3684Z" fill="${color}"/><path d="M18.0695 14.375H9.50328C9.09184 14.375 8.7583 14.7143 8.7583 15.1329V22.2301C8.7583 22.6487 9.09184 22.988 9.50328 22.988H18.0695C18.4809 22.988 18.8145 22.6487 18.8145 22.2301V15.1329C18.8145 14.7143 18.4809 14.375 18.0695 14.375Z" fill="${color}"/><path d="M18.07 33.9414H9.50377C9.09233 33.9414 8.75879 34.2807 8.75879 34.6993V41.7965C8.75879 42.2151 9.09233 42.5544 9.50377 42.5544H18.07C18.4814 42.5544 18.815 42.2151 18.815 41.7965V34.6993C18.815 34.2807 18.4814 33.9414 18.07 33.9414Z" fill="${color}"/></g></svg>`;
  }

  findLocationAtTime(locations: any[], time: number): any {
    if (!locations || locations.length === 0) return null;

    // Find the closest location
    for (let i = 0; i < locations.length - 1; i++) {
      if (time >= locations[i].time && time <= locations[i + 1].time) {
        // Interpolate between two points
        const ratio = (time - locations[i].time) / (locations[i + 1].time - locations[i].time);
        return {
          lat: locations[i].lat + (locations[i + 1].lat - locations[i].lat) * ratio,
          lng: locations[i].lng + (locations[i + 1].lng - locations[i].lng) * ratio,
          speed: locations[i].speed
        };
      }
    }

    // Return closest location
    if (time < locations[0].time) return locations[0];
    return locations[locations.length - 1];
  }

  fitMapBounds(): void {
    const bounds = new google.maps.LatLngBounds();
    
    this.vehiclePolylines.forEach(polyline => {
      const path = polyline.getPath();
      path.forEach(latLng => {
        bounds.extend(latLng);
      });
    });

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds);
    }
  }

  clearMap(): void {
    // Clear polylines
    this.vehiclePolylines.forEach(polyline => polyline.setMap(null));
    this.vehiclePolylines.clear();

    // Clear vehicle markers
    this.vehicleMarkers.forEach(marker => marker.setMap(null));
    this.vehicleMarkers.clear();

    // Clear milestone markers
    this.milestoneMarkers.forEach(marker => marker.setMap(null));
    this.milestoneMarkers = [];

    // Clear trip flag markers
    this.tripFlagMarkers.forEach(marker => marker.setMap(null));
    this.tripFlagMarkers = [];
  }

  ngOnDestroy(): void {
    this.clearMap();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
